import { useState } from "react";
import { ArrowUpRight, Phone, Mail, MapPin, Instagram, Facebook } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { useSite } from "@/context/SiteContext";
import { LLink } from "@/components/common";
import { BOOKING_URL, trackBookingClick } from "@/lib/booking";
import { openConsentSettings } from "@/lib/consent";
import { BiosphereSeal } from "@/components/BiosphereSeal";
import { Logo } from "./Logo";
import { LANDINGS } from "@/i18n/landings";

const LANDING_KEYS = ["nearSeat", "nearMontserrat", "parkingBarcelona", "weddingsBaix"];

export function Footer() {
  const { t, lang } = useLang();
  const landings = LANDINGS[lang] || LANDINGS.es;
  const { settings } = useSite();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="bg-ink text-bone pt-20 pb-28 md:pb-12">
      <div className="mx-auto max-w-site px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 border-b border-white/10 pb-16">
          <div className="lg:col-span-4">
            <Logo light />
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-bone/60">{t.footer.tagline}</p>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-book-button" onClick={() => trackBookingClick("footer-book-button", lang)} className="btn btn-light mt-8">
              {t.nav.book} <ArrowUpRight size={13} />
            </a>
            <BiosphereSeal onDark className="mt-10" />
            {(settings.instagram || settings.facebook) && (
              <div className="mt-10 flex items-center gap-4" data-testid="footer-social">
                {settings.instagram && (
                  <a href={settings.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="flex h-10 w-10 items-center justify-center border border-white/15 text-bone/70 hover:text-gold hover:border-gold/50 transition-colors">
                    <Instagram size={18} strokeWidth={1.5} />
                  </a>
                )}
                {settings.facebook && (
                  <a href={settings.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="flex h-10 w-10 items-center justify-center border border-white/15 text-bone/70 hover:text-gold hover:border-gold/50 transition-colors">
                    <Facebook size={18} strokeWidth={1.5} />
                  </a>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <p className="eyebrow !text-gold mb-6">{t.footer.explore}</p>
            <ul className="space-y-3 text-sm text-bone/75">
              <li><LLink to="rooms" className="link-line hover:text-bone">{t.nav.rooms}</LLink></li>
              <li><LLink to="restaurant" className="link-line hover:text-bone">{t.nav.restaurant}</LLink></li>
              <li><LLink to="location" className="link-line hover:text-bone">{t.nav.location}</LLink></li>
              <li><LLink to="gallery" className="link-line hover:text-bone">{t.nav.gallery}</LLink></li>
              <li><LLink to="biosphere" className="link-line hover:text-bone">{t.nav.biosphere}</LLink></li>
              <li><LLink to="blog" className="link-line hover:text-bone">{t.nav.blog}</LLink></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="eyebrow !text-gold mb-6">{t.footer.business}</p>
            <ul className="space-y-3 text-sm text-bone/75">
              <li><LLink to="corporate" className="link-line hover:text-bone">{t.nav.corporate}</LLink></li>
              <li><LLink to="meetingRooms" className="link-line hover:text-bone">{t.corporate.salasEyebrow}</LLink></li>
              <li><LLink to="events" className="link-line hover:text-bone">{t.nav.events}</LLink></li>
              <li><LLink to="weddings" className="link-line hover:text-bone">{t.events.types[0].t}</LLink></li>
              <li><LLink to="contact" className="link-line hover:text-bone">{t.nav.contact}</LLink></li>
            </ul>
            <p className="eyebrow !text-gold mb-6 mt-10">{t.footer.guides}</p>
            <ul className="space-y-3 text-sm text-bone/75" data-testid="footer-guides">
              {LANDING_KEYS.map((k) => (
                <li key={k}><LLink to={k} data-testid={`footer-guide-${k}`} className="link-line hover:text-bone">{landings[k].eyebrow}</LLink></li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="eyebrow !text-gold mb-6">{t.footer.contact}</p>
            <ul className="space-y-3 text-sm text-bone/75">
              <li className="flex gap-3"><MapPin size={15} className="mt-0.5 shrink-0 text-gold" /><span>{settings.address_line || "Av. Pau Claris s/n"}, {settings.postal_code || "08760"} {settings.city || "Martorell, Barcelona"}</span></li>
              <li className="flex gap-3"><Phone size={15} className="mt-0.5 shrink-0 text-gold" /><a href={`tel:${settings.phone_href || "+34937745160"}`} data-testid="footer-phone" className="link-line hover:text-bone">{settings.phone || "+34 937 745 160"}</a></li>
              <li className="flex gap-3"><Mail size={15} className="mt-0.5 shrink-0 text-gold" /><a href={`mailto:${settings.email || "info@hotel-martorell.com"}`} data-testid="footer-email" className="link-line hover:text-bone">{settings.email || "info@hotel-martorell.com"}</a></li>
            </ul>
            <div className="mt-8">
              <p className="text-sm text-bone/85">{t.footer.newsletter}</p>
              <p className="text-xs text-bone/50 mt-1">{t.footer.newsletterText}</p>
              {sent ? (
                <p data-testid="newsletter-success" className="mt-4 text-sm text-gold">{t.footer.subscribed}</p>
              ) : (
                <form
                  className="mt-4 flex border-b border-white/25 focus-within:border-gold transition-colors"
                  onSubmit={(e) => { e.preventDefault(); if (email.includes("@")) setSent(true); }}
                >
                  <input data-testid="newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@ejemplo.com" className="flex-1 bg-transparent py-3 text-sm text-bone placeholder:text-bone/30 outline-none" />
                  <button data-testid="newsletter-submit" type="submit" className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold hover:text-bone transition-colors">{t.footer.subscribe}</button>
                </form>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col md:flex-row md:items-center justify-between gap-4 text-[11px] font-mono tracking-[0.12em] text-bone/45">
          <p>© {year} Hotel Ciutat Martorell. {t.footer.rights} · {t.footer.registration} {settings.registration || "HB-004205"}</p>
          <div className="flex gap-6">
            <LLink to="legal" data-testid="footer-legal" className="hover:text-bone transition-colors">{t.footer.legalNotice}</LLink>
            <LLink to="privacy" data-testid="footer-privacy" className="hover:text-bone transition-colors">{t.footer.privacy}</LLink>
            <LLink to="cookies" data-testid="footer-cookies" className="hover:text-bone transition-colors">{t.footer.cookies}</LLink>
            <button type="button" onClick={openConsentSettings} data-testid="footer-cookie-settings" className="hover:text-bone transition-colors text-left">{t.footer.cookieSettings}</button>
          </div>
        </div>
      </div>
    </footer>
  );
}
